import { C2PAAdapter } from '@upl/c2pa-adapter';
import {
  EvidenceAdapterRegistry,
  NON_ACTIVE_ADAPTER_DESCRIPTORS,
  VerificationOrchestrator,
} from '@upl/evidence-adapter-runtime';
import { MetadataEvidenceAdapter } from '@upl/metadata-adapter';
import type { VerificationResult } from '@upl/evidence-schema';
import { sanitizeArtifactFilename, type ArtifactInput } from '@upl/provenance-core';
import { durationBucket, emitOperationalEvent } from './analytics.js';
import { InputValidationError, validateInputDescriptor } from './file-validation.js';

const registry = new EvidenceAdapterRegistry();
registry.register(new C2PAAdapter());
registry.register(new MetadataEvidenceAdapter());
for (const descriptor of NON_ACTIVE_ADAPTER_DESCRIPTORS) registry.registerDescriptor(descriptor);
const orchestrator = new VerificationOrchestrator(registry, {
  runtime: 'BROWSER',
  mode: 'LOCAL_BROWSER',
  timeoutMs: 90_000,
  privacy: {
    processed_locally: true,
    uploaded: false,
    stored: false,
    original_media_retained: false,
  },
});
const HEADER_READ_LIMIT = 1024 * 1024;

export interface VerificationProgress {
  stage: 'validating' | 'credentials' | 'normalizing';
  label: string;
}

export async function verifyLocalImage(
  file: File,
  onProgress?: (progress: VerificationProgress) => void,
  verificationContext?: string,
): Promise<VerificationResult> {
  const started = performance.now();
  emitOperationalEvent('verification_started');
  try {
    onProgress?.({ stage: 'validating', label: 'Validating file structure' });
    const header = new Uint8Array(await file.slice(0, Math.min(file.size, HEADER_READ_LIMIT)).arrayBuffer());
    const filename = sanitizeArtifactFilename(file.name);
    const validation = validateInputDescriptor({
      filename,
      size: file.size,
      declaredMime: file.type,
      header,
    });
    // Probe 001 uses the trusted server verification boundary when optional
    // measurement is enabled. The file is processed ephemerally and never stored.
    if (verificationContext) {
      const form = new FormData();
      form.set('artifact', file);
      const response = await fetch('/v1/verify', { method: 'POST', headers: { 'X-PRVBL-Telemetry-Context': verificationContext }, body: form });
      if (!response.ok) throw new Error('Measured verification boundary unavailable');
      const result = await response.json() as VerificationResult;
      onProgress?.({ stage: 'normalizing', label: 'Building the evidence report' });
      emitOperationalEvent('verification_completed', { durationBucket: durationBucket(performance.now() - started) });
      for (const signalType of new Set(result.signals.map(({ kind }) => kind))) {
        emitOperationalEvent('signal_type_detected', { signalType });
      }
      return result;
    }
    const bytes = new Uint8Array(await file.arrayBuffer());
    const input: ArtifactInput = {
      bytes,
      filename,
      declaredMediaType: file.type,
      detectedMediaType: validation.detectedMime,
    };
    const verificationId = crypto.randomUUID();
    const startedAt = new Date().toISOString();

    onProgress?.({ stage: 'credentials', label: 'Inspecting Content Credentials locally' });
    const result = await orchestrator.verify({ artifact: input, verificationId, startedAt });
    onProgress?.({ stage: 'normalizing', label: 'Building the evidence report' });
    emitOperationalEvent('verification_completed', { durationBucket: durationBucket(performance.now() - started) });
    for (const signalType of new Set(result.signals.map(({ kind }) => kind))) {
      emitOperationalEvent('signal_type_detected', { signalType });
    }
    return result;
  } catch (error) {
    emitOperationalEvent(error instanceof InputValidationError && error.code === 'UNSUPPORTED_FORMAT' ? 'unsupported_format' : 'verification_failed');
    throw error;
  }
}
